import {
  createMediator,
  type Mediator,
  type MediatorTransitionResult,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";
import { MIX_CATALOG_HREF } from "@/shared/lib/mixReturn";

export const HEADER_SCROLL_THRESHOLD = 5;
export const HEADER_TOP_OFFSET = 10;

export interface HeaderState {
  readonly isSearchAvailable?: boolean;
  readonly isSearchOpen: boolean;
  readonly isHeaderVisible: boolean;
  readonly lastScrollY: number;
}

export type HeaderEvent =
  | {
      readonly type: "header.navigation-requested";
      readonly isThreadView: boolean;
      readonly canGoBack: boolean;
      readonly fromMixCatalog: boolean;
    }
  | {
      readonly type: "header.search-toggle";
      readonly scrollY: number;
    }
  | {
      readonly type: "header.search-availability-changed";
      readonly isSearchAvailable: boolean;
    }
  | {
      readonly type: "header.scroll";
      readonly scrollY: number;
    }
  | {
      readonly type: "header.back-requested";
      readonly canGoBack: boolean;
      readonly fromMixCatalog: boolean;
    }
  | {
      readonly type: "side-menu.open";
    };

export type HeaderInputEvent =
  | { readonly type: "header.navigation-requested" }
  | { readonly type: "header.search-toggle" }
  | { readonly type: "header.back-requested" }
  | { readonly type: "side-menu.open" };

export type HeaderEffect =
  | { readonly type: "side-menu.open" }
  | { readonly type: "history.back" }
  | { readonly type: "location.assign"; readonly href: string }
  | { readonly type: "router.navigate-root" };

export interface HeaderMediatorOptions {
  readonly initialScrollY?: number;
  readonly runEffect?: (effect: HeaderEffect) => void;
}

export function isHeaderEvent(event: TypedEvent): event is HeaderEvent {
  switch (event.type) {
    case "header.navigation-requested":
      return (
        hasBooleanField(event, "isThreadView") &&
        hasBooleanField(event, "canGoBack") &&
        hasBooleanField(event, "fromMixCatalog")
      );
    case "header.search-toggle":
    case "header.scroll":
      return hasNumberField(event, "scrollY");
    case "header.search-availability-changed":
      return hasBooleanField(event, "isSearchAvailable");
    case "header.back-requested":
      return (
        hasBooleanField(event, "canGoBack") &&
        hasBooleanField(event, "fromMixCatalog")
      );
    case "side-menu.open":
      return true;
    default:
      return false;
  }
}

function hasNumberField(event: TypedEvent, field: string): boolean {
  return typeof (event as Record<string, unknown>)[field] === "number";
}

function hasBooleanField(event: TypedEvent, field: string): boolean {
  return typeof (event as Record<string, unknown>)[field] === "boolean";
}

export function transitionHeaderEvent(
  state: HeaderState,
  event: HeaderEvent,
): MediatorTransitionResult<HeaderState, HeaderEffect> {
  switch (event.type) {
    case "header.navigation-requested":
      return {
        status: "handled",
        state,
        effects: [
          event.isThreadView
            ? resolveBackEffect(event)
            : { type: "side-menu.open" },
        ],
      };
    case "header.search-toggle":
      if (state.isSearchAvailable === false) {
        return { status: "handled", state };
      }
      return {
        status: "handled",
        state: {
          ...state,
          isSearchOpen: !state.isSearchOpen,
          isHeaderVisible: true,
          lastScrollY: event.scrollY,
        },
      };
    case "header.search-availability-changed":
      return {
        status: "handled",
        state: {
          ...state,
          isSearchAvailable: event.isSearchAvailable,
          isSearchOpen: event.isSearchAvailable && state.isSearchOpen,
        },
      };
    case "header.scroll":
      return {
        status: "handled",
        state: transitionScroll(state, event.scrollY),
      };
    case "header.back-requested":
      return {
        status: "handled",
        state,
        effects: [resolveBackEffect(event)],
      };
    case "side-menu.open":
      return { status: "unhandled" };
  }
}

function transitionScroll(
  state: HeaderState,
  currentScrollY: number,
): HeaderState {
  if (state.isSearchOpen) {
    return {
      ...state,
      lastScrollY: currentScrollY,
    };
  }

  const diff = currentScrollY - state.lastScrollY;
  let isHeaderVisible = state.isHeaderVisible;
  if (currentScrollY < HEADER_TOP_OFFSET) {
    isHeaderVisible = true;
  } else if (diff > HEADER_SCROLL_THRESHOLD) {
    isHeaderVisible = false;
  } else if (diff < -HEADER_SCROLL_THRESHOLD) {
    isHeaderVisible = true;
  }

  return {
    ...state,
    isHeaderVisible,
    lastScrollY: currentScrollY,
  };
}

function resolveBackEffect(event: {
  canGoBack: boolean;
  fromMixCatalog: boolean;
}): HeaderEffect {
  if (event.canGoBack) return { type: "history.back" };
  if (event.fromMixCatalog) {
    return { type: "location.assign", href: MIX_CATALOG_HREF };
  }
  return { type: "router.navigate-root" };
}

export function createHeaderMediator(
  options: HeaderMediatorOptions = {},
): Mediator<HeaderState, HeaderEvent, HeaderEffect> {
  return createMediator<HeaderState, HeaderEvent, HeaderEffect>(
    {
      isSearchOpen: false,
      isHeaderVisible: true,
      lastScrollY: options.initialScrollY ?? 0,
    },
    transitionHeaderEvent,
    { runEffect: options.runEffect },
  );
}
